import { useTranslation } from "react-i18next"

import type { PlanUpgradePayload } from "@/api/fair-use"

import { Lock } from "@/components/icons"
import { Button } from "@/components/ui/button"

import { planFeatureLabelKey } from "../fair-use-plan-utils"
import { useFairUseStore } from "../fair-use-store"

/**
 * Whether the refused count resets with the month (images) or is a standing
 * total the user can free up themselves (saved scheduled flows). Same numbers,
 * two different sentences — "you have used 5 of 5 this month" would be wrong
 * about a schedule the user can delete right now.
 */
type PlanUpgradeCountScope = "month" | "total"

const COUNT_COPY: Record<PlanUpgradeCountScope, { plan: string; noPlan: string }> = {
  month: {
    plan: "{{feature}}: you have used {{used}} of {{limit}} this month on the {{plan}} plan.",
    noPlan: "{{feature}}: you have used {{used}} of {{limit}} this month.",
  },
  total: {
    plan: "{{feature}}: you are using {{used}} of {{limit}} on the {{plan}} plan.",
    noPlan: "{{feature}}: you are using {{used}} of {{limit}}.",
  },
}

const COUNT_KEYS: Record<PlanUpgradeCountScope, { plan: string; noPlan: string }> = {
  month: {
    plan: "fairUse.planUpgrade.limitReached",
    noPlan: "fairUse.planUpgrade.limitReachedNoPlan",
  },
  total: {
    plan: "fairUse.planUpgrade.limitReachedTotal",
    noPlan: "fairUse.planUpgrade.limitReachedTotalNoPlan",
  },
}

type PlanUpgradeNoticeProps = {
  /**
   * The refusal to render. OMITTED by the chat card, which reads the frame the
   * WS bridge parked on the store; the REST and activity surfaces parse their
   * own payload out of the response they were handed and pass it here, because
   * neither one travels through the bridge.
   *
   * `null` is not the same as omitting it: it means "this surface has its own
   * refusal and could not parse it", and renders the generic plan copy. Falling
   * back to the store there would name whatever feature was refused last in
   * this tab, which is very likely a different one.
   */
  payload?: PlanUpgradePayload | null
  /** See {@link PlanUpgradeCountScope}. Defaults to the monthly wording. */
  countScope?: PlanUpgradeCountScope
  /**
   * Whether to offer "See your plan". It opens the budget popover, which is
   * mounted by the chat composer (`UsageIndicator`) — on a surface that has no
   * composer, such as the flows landing page, the button would be inert, so
   * that surface passes false rather than showing an action that does nothing.
   */
  showSeePlan?: boolean
}

/**
 * What a `plan_upgrade_required` refusal looks like — in the chat log, in the
 * flows schedule dialog and in the activity log (fair use round 2, #3087). One
 * card for all three, so a plan refusal reads the same wherever the user meets
 * it.
 *
 * Deliberately NOT the destructive error card and NOT the budget gate:
 * - nothing failed, so red would be wrong (DESIGN.md reserves destructive for
 *   errors and deletes); this takes the same amber warning treatment as the
 *   budget gate, because both are policy states the user can act on;
 * - the budget gate's one action, "Request more", is actively WRONG here. More
 *   SEK does not add Deep Explorer to a Standard plan, and sending the user
 *   into the request-more flow would produce a request no admin can grant.
 *   The action offered is "see your plan" — it opens the budget popover, which
 *   names the plan and lists exactly what the plan locks.
 *
 * In the chat log the refusal arrives through `routeFairUseError`, which parks
 * the payload on the fair-use store (the card is handed a bare `errorCode`
 * string and cannot see the frame). The other two surfaces never touch the
 * bridge and pass their own parsed `payload` in. With no payload — a hydrated
 * session whose
 * refusal happened in another tab, or a frame that carried only the code — the
 * copy degrades to the generic "this feature" wording rather than rendering
 * `undefined` at the user.
 */
export function PlanUpgradeNotice({
  payload: given,
  countScope = "month",
  showSeePlan = true,
}: PlanUpgradeNoticeProps = {}) {
  const { t } = useTranslation()
  const parked = useFairUseStore((state) => state.planUpgrade)
  const openPopover = useFairUseStore((state) => state.openPopover)
  const payload = given === undefined ? parked : given

  const feature = t(planFeatureLabelKey(payload?.feature), {
    defaultValue: "This feature",
  })
  const plan = payload?.plan
    ? t(`fairUse.plans.${payload.plan}`, { defaultValue: payload.plan })
    : undefined

  // A counted cap (images this month, scheduled flows) refuses with numbers,
  // and the numbers ARE the explanation — "5 of 5 used" answers the question
  // that "not included in your plan" leaves open.
  const hasCount = typeof payload?.limit === "number" && typeof payload.used === "number"
  let body: string
  if (hasCount) {
    const variant = plan ? "plan" : "noPlan"
    body = t(COUNT_KEYS[countScope][variant], {
      feature,
      plan,
      used: payload.used,
      limit: payload.limit,
      defaultValue: COUNT_COPY[countScope][variant],
    })
  } else if (plan) {
    body = t("fairUse.planUpgrade.body", {
      feature,
      plan,
      defaultValue: "{{feature}} is not included in your {{plan}} plan.",
    })
  } else {
    body = t("fairUse.planUpgrade.bodyNoPlan", {
      feature,
      defaultValue: "{{feature}} is not included in your plan.",
    })
  }

  const requiredPlans = (payload?.required_plans ?? [])
    .map((id) => t(`fairUse.plans.${id}`, { defaultValue: id }))
    .join(", ")

  return (
    <div
      className="border-warning/30 bg-warning/10 space-y-3 rounded-lg border p-4"
      data-testid="fair-use-plan-upgrade-notice"
    >
      <div className="flex items-start gap-3">
        <Lock className="text-warning-foreground mt-0.5 size-5 shrink-0" />
        <div className="flex-1 space-y-1">
          <p className="text-foreground text-sm font-bold">
            {t("fairUse.planUpgrade.title", { defaultValue: "Not included in your plan" })}
          </p>
          <p className="text-muted-foreground text-sm leading-relaxed">{body}</p>
          {requiredPlans && (
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t("fairUse.planUpgrade.requiredPlans", {
                plans: requiredPlans,
                defaultValue: "Available on: {{plans}}",
              })}
            </p>
          )}
          <p className="text-muted-foreground text-sm leading-relaxed">
            {t("fairUse.planUpgrade.contactAdmin", {
              defaultValue: "Ask your administrator to upgrade your plan.",
            })}
          </p>
        </div>
      </div>
      {showSeePlan && (
        <div className="border-border/30 border-t pt-3">
          <Button
            className="w-full gap-1.5 text-xs sm:w-auto"
            data-testid="fair-use-plan-upgrade-see-plan"
            size="sm"
            variant="secondary"
            onClick={() => openPopover()}
          >
            {t("fairUse.planUpgrade.seePlan", { defaultValue: "See your plan" })}
          </Button>
        </div>
      )}
    </div>
  )
}
